<template>
    <div class="min-h-screen bg-gray-100 p-6">
        <div class="max-w-4xl mx-auto">
            <!-- Header -->
            <div class="bg-white rounded-lg shadow-md p-6 mb-6">
                <div class="flex justify-between items-center">
                    <div>
                        <h1 class="text-3xl font-bold text-gray-800">Détails de la Commande</h1>
                        <p class="text-gray-600">Commande #{{ order.id }}</p>
                        <p class="text-sm text-gray-500">Table {{ tableNumber }}</p>
                    </div>
                    <div class="flex space-x-4">
                        <router-link
                                to="/serveur/tables"
                                class="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600 transition-colors"
                        >
                            Retour aux tables
                        </router-link>
                        <Notifications />
                    </div>
                </div>
            </div>

            <!-- Order Status -->
            <div class="bg-white rounded-lg shadow-md p-6 mb-6">
                <h2 class="text-xl font-semibold mb-4">Statut de la commande</h2>
                <div class="flex items-center justify-between mb-4">
                    <div class="flex space-x-4">
            <span class="px-3 py-1 rounded-full text-sm font-medium" :class="statusClass">
              {{ orderStatusText }}
            </span>
                        <span class="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
              Total: {{ order.total }}€
            </span>
                    </div>
                    <button
                            v-if="canServeOrder"
                            @click="handlerserveOrder"
                            class="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 transition-colors"
                    >
                        Marquer comme servi
                    </button>
                </div>

                <!-- Progress Steps -->
                <div class="flex justify-between items-center mb-6">
                    <div v-for="(step, index) in progressSteps" :key="step.status" class="flex flex-col items-center">
                        <div class="w-10 h-10 rounded-full flex items-center justify-center mb-2"
                             :class="getStepClass(step.status)">
                            {{ index + 1 }}
                        </div>
                        <span class="text-xs text-gray-600">{{ step.label }}</span>
                    </div>
                </div>
            </div>

            <!-- Order Items -->
            <div class="bg-white rounded-lg shadow-md p-6 mb-6">
                <h2 class="text-xl font-semibold mb-4">Articles de la commande</h2>

                <div class="space-y-4">
                    <div
                            v-for="item in order.items"
                            :key="item.id"
                            class="border border-gray-200 rounded-lg p-4"
                    >
                        <div class="flex justify-between items-start mb-2">
                            <div>
                                <h3 class="font-semibold text-lg">{{ item.name }}</h3>
                                <p class="text-gray-600">{{ item.price }}€ x {{ item.quantity }}</p>
                                <p class="text-sm text-gray-500">Sous-total: {{ (item.price * item.quantity).toFixed(2) }}€</p>
                            </div>
                            <span class="px-3 py-1 rounded-full text-sm font-medium" :class="getItemStatusClass(item.status)">
                {{ getItemStatusText(item.status) }}
              </span>
                        </div>

                        <!-- Item Progress -->
                        <div class="mt-3">
                            <div class="flex justify-between text-xs text-gray-500 mb-1">
                                <span>Progression</span>
                                <span>{{ getItemStatusText(item.status) }}</span>
                            </div>
                            <div class="w-full bg-gray-200 rounded-full h-2">
                                <div class="bg-blue-600 h-2 rounded-full transition-all duration-300"
                                     :style="{ width: getItemProgress(item.status) + '%' }"></div>
                            </div>
                        </div>

                        <!-- Item Actions -->
                        <div v-if="item.status === 'ready'" class="mt-3 p-3 bg-green-50 rounded-lg">
                            <p class="text-green-700 text-sm">✓ Prêt à être servi</p>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Order Timeline -->
            <div class="bg-white rounded-lg shadow-md p-6">
                <h2 class="text-xl font-semibold mb-4">Historique de la commande</h2>

                <div class="space-y-4">
                    <div
                            v-for="event in orderTimeline"
                            :key="event.id"
                            class="flex items-start space-x-4"
                    >
                        <div class="w-3 h-3 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                        <div class="flex-1">
                            <p class="text-gray-800">{{ event.message }}</p>
                            <p class="text-xs text-gray-500">{{ formatTime(event.timestamp) }}</p>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Cancellation Section -->
            <div v-if="order.status === 'active'" class="bg-white rounded-lg shadow-md p-6 mt-6 border border-red-200">
                <h2 class="text-xl font-semibold mb-4 text-red-700">Annulation de commande</h2>
                <p class="text-gray-600 mb-4">
                    En cas d'annulation, une validation sera demandée aux stations concernées.
                </p>
                <button
                        @click="requestCancellation"
                        class="bg-red-600 text-white px-6 py-2 rounded hover:bg-red-700 transition-colors"
                >
                    Demander l'annulation
                </button>
            </div>
        </div>
    </div>
</template>

<script>
    import { mapState,  mapActions } from 'vuex';
    import Notifications from './notifications.vue';

    export default {
        name: 'CommandeDetails',
        components: {
            Notifications
        },
        props: {
            orderId: {
                type: [String, Number],
                required: true
            }
        },
        data() {
            return {
                progressSteps: [
                    { status: 'pending', label: 'En attente' },
                    { status: 'in_progress', label: 'En préparation' },
                    { status: 'ready', label: 'Prêt' },
                    { status: 'served', label: 'Servi' }
                ]
            };
        },
        computed: {
            ...mapState(['orders', 'tables', 'notifications']),
            order() {
                const foundOrder = this.orders.find(o => o.id === parseInt(this.orderId));
                if (!foundOrder) {
                    // Retourner une commande par défaut si non trouvée
                    return {
                        id: this.orderId,
                        tableId: 0,
                        items: [],
                        total: 0,
                        status: 'active',
                        createdAt: new Date()
                    };
                }
                return foundOrder;
            },
            tableNumber() {
                const table = this.tables.find(t => t.id === this.order.tableId);
                return table ? table.number : 'N/A';
            },
            orderStatusText() {
                const statusMap = {
                    'active': 'En cours',
                    'completed': 'Terminée',
                    'cancelled': 'Annulée'
                };
                return statusMap[this.order.status] || this.order.status;
            },
            statusClass() {
                const classes = {
                    'active': 'bg-green-100 text-green-800',
                    'completed': 'bg-gray-100 text-gray-800',
                    'cancelled': 'bg-red-100 text-red-800'
                };
                return classes[this.order.status] || 'bg-gray-100 text-gray-800';
            },
            canServeOrder() {
                return this.order.status === 'active' &&
                    this.order.items.some(item => item.status === 'ready') &&
                    !this.order.items.some(item => item.status === 'pending' || item.status === 'in_progress');
            },
            orderTimeline() {
                const timeline = [];

                // Ajouter la création de la commande
                timeline.push({
                    id: 'creation',
                    message: `Commande créée pour la table ${this.tableNumber}`,
                    timestamp: this.order.createdAt
                });

                // Ajouter les mises à jour des items
                this.order.items.forEach(item => {
                    if (item.updatedAt && item.updatedAt !== item.createdAt) {
                        timeline.push({
                            id: `item-${item.id}-update`,
                            message: `${item.name} - ${this.getItemStatusText(item.status)}`,
                            timestamp: item.updatedAt
                        });
                    }
                });

                // Trier par timestamp
                return timeline.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
            }
        },
        methods: {
            ...mapActions(['serveOrder', 'updateItemStatus']),

            getItemStatusClass(status) {
                const classes = {
                    'pending': 'bg-orange-100 text-orange-800',
                    'in_progress': 'bg-blue-100 text-blue-800',
                    'ready': 'bg-green-100 text-green-800',
                    'served': 'bg-gray-100 text-gray-800'
                };
                return classes[status] || 'bg-gray-100 text-gray-800';
            },

            getItemStatusText(status) {
                const statusMap = {
                    'pending': 'En attente',
                    'in_progress': 'En préparation',
                    'ready': 'Prêt à servir',
                    'served': 'Servi'
                };
                return statusMap[status] || status;
            },

            getItemProgress(status) {
                const progressMap = {
                    'pending': 25,
                    'in_progress': 50,
                    'ready': 75,
                    'served': 100
                };
                return progressMap[status] || 0;
            },

            getStepClass(status) {
                const currentStepIndex = this.progressSteps.findIndex(step =>
                    this.order.items.some(item => item.status === step.status)
                );
                const stepIndex = this.progressSteps.findIndex(step => step.status === status);

                if (stepIndex <= currentStepIndex) {
                    return 'bg-blue-600 text-white';
                } else {
                    return 'bg-gray-200 text-gray-500';
                }
            },

            formatTime(timestamp) {
                return new Date(timestamp).toLocaleString('fr-FR', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                });
            },

            async handlerserveOrder() {
                try {
                    await this.serveOrder(this.order.id);
                    alert('Commande marquée comme servie avec succès !');
                } catch (error) {
                    alert('Erreur lors du service de la commande: ' + error.message);
                }
            },

            requestCancellation() {
                if (confirm('Êtes-vous sûr de vouloir annuler cette commande ? Une validation sera demandée aux stations.')) {
                    // Simuler la demande d'annulation
                    const hasBarItems = this.order.items.some(item => item.type === 'bar' && item.status !== 'served');
                    const hasKitchenItems = this.order.items.some(item => item.type === 'cuisine' && item.status !== 'served');

                    let message = 'Demande d\'annulation envoyée';
                    if (hasBarItems) message += ' au bar';
                    if (hasBarItems && hasKitchenItems) message += ' et';
                    if (hasKitchenItems) message += ' à la cuisine';

                    alert(message + '. En attente de validation.');
                }
            }
        },
        mounted() {
            console.log('Commande chargée:', this.order);
        }
    };
</script>